import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ago } from './CalendarFeeds';
import { useSettings } from '../pages/settings/SettingsContext';
import './TodoEmailSettings.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Settings -> Apps & content -> To do & calendars: EMAIL TO TO-DO and PRIVATE
 * CALENDAR LINKS (migration 097).
 *
 * Gordon, 28 October. Three switches and a log, drawn like the other rules
 * (AppRules.css's ar-* classes) so the page reads as one:
 *
 *   Email to to-do             off until the MX record and the webhook are
 *                              in place; the address is shown beside it with
 *                              the setup steps, so the switch is never
 *                              turned on before the mail can arrive
 *   Contractors may email      on - Gordon's decision - and only inside the
 *   to-dos in                  switch above
 *   Last 20 emails received    when, from, what came of it: to-do created /
 *                              unknown sender / wrong address / switched
 *                              off / failed. An unknown sender is shown by
 *                              address only.
 *   Private calendar links     on; off stops every link at once
 *
 *   When an email arrives     (106) wait to be sorted with AI suggestions /
 *                              wait to be sorted / become a to-do straight
 *                              away - two rules behind three answers
 *                              (todo_email_mode, todo_email_ai)
 *
 * Reads GET /app-settings through the Settings shell (useSettings), writes
 * PUT /app-settings/<rule>, and asks the shell to reload so the search and
 * the Overview agree. The log is GET /inbound/log; 503 means the migration
 * has not run, and the card says so instead of its rows.
 */
const OUTCOME = {
  created: ['To-do created', 'is-good'],
  // 106: on the sender's Emails page, or dismissed from it.
  waiting: ['Waiting to be sorted', 'is-wait'],
  dismissed: ['Dismissed', 'is-quiet'],
  unknown_sender: ['Unknown sender', 'is-quiet'],
  wrong_address: ['Wrong address', 'is-quiet'],
  disabled: ['Switched off', 'is-quiet'],
  failed: ['Failed', 'is-bad'],
  refused_auth: ['Refused: sender not verified', 'is-bad'],
  pending: ['Being read…', 'is-quiet']
};

/**
 * WHEN AN EMAIL ARRIVES (106): three answers over two rules - todo_email_mode
 * ('sort' | 'direct') and todo_email_ai (whether Claude may suggest). The
 * first two differ only by the AI switch.
 */
const ARRIVES = [
  { key: 'sort-ai', mode: 'sort', ai: true, label: 'Wait to be sorted, with AI suggestions',
    words: 'The email waits on the sender’s Emails page; Claude reads it and proposes the to-dos and sub-items it calls for, which the person ticks, edits and creates. Claude is sent the sender, the subject, the text and the names of the person’s lists - only for people whose AI switch (Features) allows it.' },
  { key: 'sort', mode: 'sort', ai: false, label: 'Wait to be sorted',
    words: 'The email waits on the sender’s Emails page to be sorted by hand: blank cards to fill, or one to-do of the whole email. Nothing is sent to Claude.' },
  { key: 'direct', mode: 'direct', ai: null, label: 'Become a to-do straight away',
    words: 'One to-do on arrival, as before: the subject as its title, the message as its notes, the files on it. Each person may still choose to wait instead, from their Emails page.' }
];

const SETUP = [
  ['Resend › Receiving', 'add the domain todo.ncmb.ai and put the MX record it shows on the todo host of the ncmb.ai DNS (lowest priority).'],
  ['Resend › Webhooks', 'add an endpoint for the event email.received pointing at this server’s /api/inbound/resend, and copy its signing secret into the server as RESEND_WEBHOOK_SECRET (RESEND_API_KEY is already set for sending).'],
  ['Here', 'switch “Email to to-do” on, then send a test email from a staff address.']
];

export default function TodoEmailSettings({ canEdit = true }) {
  const { rules, reloadRules } = useSettings();
  const [saving, setSaving] = useState(null);
  const [problem, setProblem] = useState(null);
  const [log, setLog] = useState(null);         // { address, emails } | { notReady } | { failed }

  const loadLog = useCallback(() => axios.get(`${API_BASE}/inbound/log`)
    .then((r) => setLog({ address: r.data?.address || 'jarvis@todo.ncmb.ai', emails: Array.isArray(r.data?.emails) ? r.data.emails : [] }))
    .catch((err) => setLog(err?.response?.status === 503 ? { notReady: true } : { failed: true })), []);
  useEffect(() => { loadLog(); }, [loadLog]);

  const setRule = async (key, path, value) => {
    setSaving(key); setProblem(null);
    try {
      await axios.put(`${API_BASE}/app-settings/${path}`, { value });
      reloadRules();
    } catch (err) {
      setProblem(err?.response?.data?.error || 'Could not save that setting');
    } finally { setSaving(null); }
  };

  const on = Boolean(rules?.todo_email_enabled);
  const contractors = rules ? rules.todo_email_contractors !== false : true;
  const requireAuth = rules ? rules.todo_email_require_auth !== false : true;
  const links = rules ? rules.calendar_links_enabled !== false : true;
  const ready = rules?.setup ? rules.setup.m097_todo_email_calendar !== false : true;
  const sortReady = rules?.setup ? rules.setup.m106_todo_email_sort !== false : true;
  const mode = rules?.todo_email_mode === 'direct' ? 'direct' : 'sort';
  const ai = rules ? rules.todo_email_ai !== false : true;
  const arrives = mode === 'direct' ? 'direct' : ai ? 'sort-ai' : 'sort';
  const address = (log && log.address) || 'jarvis@todo.ncmb.ai';
  const busy = !canEdit || !ready;

  /** One radio chosen: the mode, and the AI switch when the choice says. Two saves at most. */
  const setArrives = async (key) => {
    const want = ARRIVES.find((a) => a.key === key);
    if (!want) return;
    setSaving('todo_email_mode'); setProblem(null);
    try {
      if (want.mode !== mode) await axios.put(`${API_BASE}/app-settings/todo-email-mode`, { value: want.mode });
      if (want.ai !== null && want.ai !== ai) await axios.put(`${API_BASE}/app-settings/todo-email-ai`, { value: want.ai });
      reloadRules();
    } catch (err) {
      setProblem(err?.response?.data?.error || 'Could not save that setting');
    } finally { setSaving(null); }
  };

  return (
    <section className="stn-area tes" aria-label="Email to to-do and calendar links">
      <h3 className="stn-area-title">Email in, calendars out</h3>
      {!ready && (
        <p className="stn-banner is-info tes-notready" data-tes-notready="" role="note">
          Email to to-do and private calendar links are waiting for a database update on the server:
          run <code>npm run migrate:097</code>. Until then the switches are read-only.
        </p>
      )}
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}

      <div className="ar-rule" id="s-todo-email" data-rule="todo-email">
        <label className="ar-check">
          <input type="checkbox" checked={on} disabled={busy || saving === 'todo_email_enabled'}
                 onChange={() => setRule('todo_email_enabled', 'todo-email-enabled', !on)} />
          <span className="ar-label">Email to to-do</span>
        </label>
        <p className="ar-consequence">
          {on
            ? <>On: an email forwarded to <code className="tes-address" data-tes-address="">{address}</code> by a member of staff
              becomes a to-do in their Inbox — the subject as the title (Fw: and Re: taken off), the message as the
              notes, the attachments as its files, marked “From email” with the sender and date. In the subject,
              <code>due: next Tuesday</code> sets the date and <code>#Clients</code> picks one of their lists.
              Mail from anybody else is logged by address and dropped; nothing is sent back.</>
            : <>Off: mail to <code className="tes-address" data-tes-address="">{address}</code> is logged as “switched off” and
              nothing is made. Switch it on once the address can receive mail:</>}
        </p>
        {!on && (
          <ol className="tes-setup" data-tes-setup="">
            {SETUP.map(([where, what]) => <li key={where}><strong>{where}</strong> — {what}</li>)}
          </ol>
        )}
      </div>

      <div className="ar-rule" id="s-todo-email-contractors" data-rule="todo-email-contractors" data-off={on ? undefined : ''}>
        <label className="ar-check">
          <input type="checkbox" checked={contractors} disabled={busy || saving === 'todo_email_contractors'}
                 onChange={() => setRule('todo_email_contractors', 'todo-email-contractors', !contractors)} />
          <span className="ar-label">Contractors may email to-dos in</span>
        </label>
        <p className="ar-consequence">
          {contractors
            ? 'On: an email from an active contractor’s address becomes a to-do in that contractor’s own Inbox, exactly as for staff. Never anybody else’s list.'
            : 'Off: a contractor’s email is logged as an unknown sender and nothing is made.'}
          {!on && ' Nothing happens either way while Email to to-do is off.'}
        </p>
      </div>

      <div className="ar-rule" id="s-todo-email-auth" data-rule="todo-email-auth" data-off={on ? undefined : ''}>
        <label className="ar-check">
          <input type="checkbox" checked={requireAuth} disabled={busy || saving === 'todo_email_require_auth'}
                 onChange={() => setRule('todo_email_require_auth', 'todo-email-require-auth', !requireAuth)} />
          <span className="ar-label">Only verified senders</span>
        </label>
        <p className="ar-consequence">
          {requireAuth
            ? 'On: an email is taken only when the receiving server could verify that it really came from the address it names - DMARC passed, or a DKIM signature from the From domain did. Anything else is logged as “Refused: sender not verified” and nothing is made; the sender is not told. A From address is otherwise a line anybody can write.'
            : 'Off: the From address alone decides. An email forged to look like it came from a member of staff would become a to-do in their Inbox.'}
          {!on && ' Nothing happens either way while Email to to-do is off.'}
        </p>
      </div>

      <fieldset className="ar-rule tes-arrives" id="s-todo-email-arrives" data-rule="todo-email-arrives" data-off={on ? undefined : ''} data-tes-arrives={arrives}>
        <legend className="ar-label">When an email arrives</legend>
        {!sortReady && (
          <p className="stn-banner is-info tes-notready" data-tes-sort-notready="" role="note">
            Sorting emails into to-dos is waiting for a database update on the server: run <code>npm run migrate:106</code>.
            Until then every email becomes a to-do straight away.
          </p>
        )}
        {ARRIVES.map((a) => (
          <label key={a.key} className={`tes-arrive${arrives === a.key ? ' is-on' : ''}`} data-tes-arrive={a.key}>
            <input type="radio" name="tes-arrives" value={a.key} checked={arrives === a.key}
                   disabled={busy || !sortReady || saving === 'todo_email_mode'} onChange={() => setArrives(a.key)} />
            <span className="tes-arrive-words">
              <span className="tes-arrive-label">{a.label}</span>
              <span className="ar-consequence tes-arrive-why">{a.words}</span>
            </span>
          </label>
        ))}
        {!on && <p className="ar-consequence">Nothing happens either way while Email to to-do is off.</p>}
      </fieldset>

      <div className="ar-rule tes-log" id="s-todo-email-log" data-rule="todo-email-log">
        <div className="tes-log-head">
          <span className="ar-label tes-log-title">Last 20 emails received</span>
          <button type="button" className="btn btn-secondary btn-small" onClick={loadLog} disabled={log === null}>Refresh</button>
        </div>
        {log === null && <p className="stn-quiet">Loading…</p>}
        {log && log.notReady && <p className="stn-quiet" data-tes-log-notready="">The log appears once migration 097 has run.</p>}
        {log && log.failed && <p className="stn-row-problem">Could not read the email log.</p>}
        {log && log.emails && log.emails.length === 0 && (
          <p className="stn-quiet" data-tes-log-empty="">No email has arrived yet.</p>
        )}
        {log && log.emails && log.emails.length > 0 && (
          <div className="tes-table-wrap">
            <table className="tes-table" data-tes-log="">
              <thead>
                <tr><th scope="col">When</th><th scope="col">From</th><th scope="col">Subject</th><th scope="col">Outcome</th></tr>
              </thead>
              <tbody>
                {log.emails.map((e) => {
                  const [word, tone] = OUTCOME[e.outcome] || [e.outcome, 'is-quiet'];
                  return (
                    <tr key={e.id} data-tes-row={e.outcome}>
                      <td><time dateTime={e.received_at} title={new Date(e.received_at).toLocaleString('en-GB')}>{ago(e.received_at)}</time></td>
                      <td className="tes-from">{e.from_address}</td>
                      <td className="tes-subject">{e.outcome === 'unknown_sender' ? <span className="stn-quiet">not kept</span> : (e.subject || <span className="stn-quiet">(no subject)</span>)}</td>
                      <td>
                        <span className={`tes-outcome ${tone}`}>{word}</span>
                        {e.error && <small className="tes-why">{e.error}</small>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ar-rule" id="s-calendar-links" data-rule="calendar-links">
        <label className="ar-check">
          <input type="checkbox" checked={links} disabled={busy || saving === 'calendar_links_enabled'}
                 onChange={() => setRule('calendar_links_enabled', 'calendar-links-enabled', !links)} />
          <span className="ar-label">Private calendar links</span>
        </label>
        <p className="ar-consequence">
          {links
            ? 'On: on the to-do Calendar, “Subscribe in your calendar” gives each person a private address that Outlook, Apple Calendar or Google Calendar can follow — their own dated to-dos (their Private list included) and the Jarvis meetings they are on. Each person chooses what theirs carries and can regenerate or remove it. Whoever holds the address can read it, so it is shown once and kept only as a fingerprint.'
            : 'Off: every private calendar address stops answering at once and nobody can make one. The links are kept, so switching back on brings them back.'}
        </p>
      </div>
    </section>
  );
}
